import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Minus, Plus, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileUrl, money } from "@/lib/api";
import { Swatch } from "@/lib/colors";
import { useCart } from "@/context/CartContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Gallery = ({ product }) => {
  const photos = useMemo(() => {
    const cover = product.cover_photo || product.photos?.[0];
    return [cover, ...(product.photos || []).filter((p) => p !== cover)].filter(Boolean);
  }, [product]);
  const [active, setActive] = useState(0);
  const go = (d) => setActive((a) => (a + d + photos.length) % photos.length);
  return (
    <div className="flex flex-col-reverse lg:flex-row gap-4" data-testid="product-gallery">
      {photos.length > 1 && (
        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:max-h-[640px] lg:w-20 flex-shrink-0">
          {photos.map((p, i) => (
            <button key={p} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
              className={`w-16 h-20 lg:w-20 lg:h-24 flex-shrink-0 overflow-hidden border transition-colors ${i === active ? "border-white" : "border-white/10 opacity-60 hover:opacity-100"}`}>
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="group relative flex-1 aspect-[4/5] bg-[#0b0b0d] overflow-hidden"
        onTouchStart={(e) => { e.currentTarget.dataset.x = e.touches[0].clientX; }}
        onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - Number(e.currentTarget.dataset.x || 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
        {photos[active] && <img key={photos[active]} src={fileUrl(photos[active])} alt={product.name} className="w-full h-full object-contain fade-up" style={{ animationDuration: ".5s" }} data-testid="gallery-main-image" />}
        {photos.length > 1 && (
          <>
            <button onClick={() => go(-1)} aria-label="Previous photo" data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"><ChevronLeft size={18} /></button>
            <button onClick={() => go(1)} aria-label="Next photo" data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"><ChevronRight size={18} /></button>
            <span className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-[10px] tracking-[0.2em] text-zinc-200 px-2.5 py-1 font-mono-ui" data-testid="gallery-counter">{active + 1} / {photos.length}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { add, items } = useCart();
  const [product, setProduct] = useState(undefined);
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const requestedColor = searchParams.get("color");
      const requested = r.data.colors.find((color) => color.name === requestedColor && color.stock > 0);
      const firstAvailable = r.data.colors.find((color) => color.stock > 0) || r.data.colors[0];
      setColor(requested?.name || firstAvailable?.name || "");
    }).catch(() => setProduct(null));
  }, [searchParams, slug]);

  if (product === undefined) return <div className="h-[70vh]" />;
  if (product === null)
    return (
      <div className="max-w-7xl mx-auto px-5 py-40 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl uppercase font-bold">Product not found</h1>
        <Link to="/shop" className="btn-ghost-store mt-8">Back to the drop</Link>
      </div>
    );

  const selected = product.colors.find((c) => c.name === color);
  const inCart = items.find((i) => i.key === `${product.id}:${color}`)?.quantity || 0;
  const available = Math.max((selected?.stock || 0) - inCart, 0);
  const soldOut = !selected || selected.stock <= 0;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16" data-testid="product-page">
      {product.status !== "published" && (
        <div className="mb-8 flex items-center gap-3 border border-[#ff2a2a]/40 bg-[#ff2a2a]/10 px-4 py-3 text-sm text-white" data-testid="product-preview-banner">
          <Eye size={16} /> Preview — this product is a draft and is not visible to customers.
        </div>
      )}
      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16">
        <Gallery product={product} />
        <div className="lg:sticky lg:top-24 self-start">
          {product.category && <p className="eyebrow">{product.category}</p>}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase text-white mt-3" data-testid="product-name">{product.name}</h1>
          <p className="text-xl text-white mt-4" data-testid="product-price">{money(product.price)}</p>

          <div className="mt-10">
            <div className="flex justify-between items-center">
              <span className="eyebrow">Color — <span className="text-white">{color}</span></span>
              <span className="text-xs text-zinc-400" data-testid="product-stock-status">
                {soldOut ? "Sold out" : selected.stock <= 5 ? `Only ${selected.stock} left` : "In stock"}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 mt-4" data-testid="product-color-options">
              {product.colors.map((c) => (
                <button key={c.name} disabled={c.stock <= 0} onClick={() => { setColor(c.name); setQty(1); }} data-testid={`color-option-${c.name.toLowerCase()}`}
                  className={`relative flex items-center gap-2 px-4 py-2.5 border text-xs tracking-wider uppercase transition-colors ${color === c.name ? "border-white text-white" : "border-white/15 text-zinc-400 hover:border-white/40"} ${c.stock <= 0 ? "opacity-50" : ""}`}>
                  <Swatch name={c.name} size={12} />
                  <span className={c.stock <= 0 ? "line-through" : ""}>{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            <div className="flex items-center border border-white/15">
              <button className="px-4 py-4 text-zinc-300 hover:text-white disabled:opacity-30" disabled={qty <= 1} onClick={() => setQty(qty - 1)} data-testid="product-qty-decrease" aria-label="Decrease"><Minus size={14} /></button>
              <span className="w-8 text-center text-sm" data-testid="product-qty">{qty}</span>
              <button className="px-4 py-4 text-zinc-300 hover:text-white disabled:opacity-30" disabled={qty >= available} onClick={() => setQty(qty + 1)} data-testid="product-qty-increase" aria-label="Increase"><Plus size={14} /></button>
            </div>
            <button className="btn-store flex-1" disabled={soldOut || available < 1} data-testid="add-to-cart-button"
              onClick={() => { add(product, color, qty, selected.stock); setQty(1); }}>
              {soldOut ? "Sold out" : available < 1 ? "All in your bag" : "Add to bag"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-4" data-testid="product-shipping-info">
            Shipping calculated at checkout.
          </p>

          {product.description && (
            <p className="mt-10 text-zinc-300 leading-relaxed whitespace-pre-line text-sm sm:text-base" data-testid="product-description">{product.description}</p>
          )}

          <Accordion type="single" collapsible className="mt-10 border-t border-white/10">
            <AccordionItem value="ship" className="border-white/10">
              <AccordionTrigger className="text-xs tracking-[0.2em] uppercase text-white hover:no-underline" data-testid="accordion-shipping">Shipping</AccordionTrigger>
              <AccordionContent className="text-zinc-400 text-sm">
                Shipping cost is shown at checkout. <Link to="/policies/shipping" className="underline text-white">Read the shipping policy</Link>.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="returns" className="border-white/10">
              <AccordionTrigger className="text-xs tracking-[0.2em] uppercase text-white hover:no-underline" data-testid="accordion-returns">Returns</AccordionTrigger>
              <AccordionContent className="text-zinc-400 text-sm">
                <Link to="/policies/returns" className="underline text-white">Read the return policy</Link>.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
